import './styles/demo.css';
import '@mantine/core/styles.css';
import appConfig from './config/appConfig';

// Inject centralized fonts as CSS custom properties
const root = document.documentElement;
root.style.setProperty('--font-heading', appConfig.fonts.heading);
root.style.setProperty('--font-body', appConfig.fonts.body);

import React, {Suspense, lazy} from 'react';
import ReactDOM from 'react-dom/client';
import {MantineProvider} from '@mantine/core';
import {
    Route,
    BrowserRouter as Router,
    Routes
} from 'react-router-dom';

// Eager shell: needed on every route, kept in main bundle so first paint is not blocked.
import {AuthProvider, ProtectedRoute} from './components/Auth';
import AdminRoute from './components/Admin/AdminRoute';
import {Toaster} from './components/Toast';
import {getSeoBaseUrl} from './utils/api';
import AuthBounceSpinner from './components/Auth/AuthBounceSpinner';

// ---- Lazy route components ----
// Importing directly from source files (NOT via barrels) so Vite/Rollup can
// code-split each route into its own chunk. This is what keeps the public
// profile bundle small: visitors of /:slug only download BlockNotePublicProfile
// and its real dependencies, not Dashboard/Designer/Chat/etc.

// Public profile — the perf-critical route.
const BlockNotePublicProfile = lazy(() => import('./components/Designer/BlockNotePublicProfile'));
const PreviewDraft = lazy(() => import('./components/Designer/BlockNotePublicProfile').then(m => ({default: m.PreviewDraft})));

// Other public routes
const ArticleViewer = lazy(() => import('./components/ArticleViewer').then(m => ({default: m.ArticleViewer})));
const ForgotPasswordPage = lazy(() => import('./components/Auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./components/Auth/ResetPasswordPage'));
const AuthCallbackPage = lazy(() => import('./components/Auth/AuthCallbackPage'));
const AuthResolve = lazy(() => import('./components/Auth/AuthResolve'));
const CalendlyCallback = lazy(() => import('./components/Auth/CalendlyCallback'));
const GumroadCallback = lazy(() => import('./components/Auth/GumroadCallback'));
const PublicProfile = lazy(() => import('./components/Designer/PublicProfile'));
const ShortLinkResolver = lazy(() => import('./components/ShortLinkResolver'));
const PublicGlobe = lazy(() => import('./components/PublicGlobe'));
const ShopifyTemplateLanding = lazy(() => import('./components/ShopifyTemplateLanding'));
const ShopifyThemeLanding = lazy(() => import('./components/ShopifyThemeLanding'));

// Protected routes
const OnboardingWizard = lazy(() => import('./components/Auth/OnboardingWizard'));
const BrandOnboardingWizard = lazy(() => import('./components/Auth/BrandOnboardingWizard'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const PerformanceAnalytics = lazy(() => import('./components/PerformanceAnalytics'));
const OverView = lazy(() => import('./components/OverView'));
const MyPages = lazy(() => import('./components/Designer/MyPages'));
const KnowledgeSources = lazy(() => import('./components/KnowledgeSources'));
const AccountSettings = lazy(() => import('./components/Settings').then(m => ({default: m.AccountSettings})));
const WhatsAppWidget = lazy(() => import('./components/WhatsAppWidget/WhatsAppWidget'));
const BlockNoteDesigner = lazy(() => import('./components/Designer/BlockNoteDesigner'));
const BlockNoteDesignerPreview = lazy(() => import('./components/Designer/BlockNoteDesigner').then(m => ({default: m.BlockNoteDesignerPreview})));
const BioProfileEditor = lazy(() => import('./components/Designer/BioProfileEditor'));
const Template = lazy(() => import('./components/Template'));
const Brands = lazy(() => import('./components/Designer/Brands'));
const BrandProducts = lazy(() => import('./components/Designer/BrandProducts'));
const OrdersList = lazy(() => import('./components/OrdersList'));
const TransactionsList = lazy(() => import('./components/TransactionsList'));
const Earnings = lazy(() => import('./components/Earnings'));
const ReferAndEarn = lazy(() => import('./components/ReferAndEarn'));
const Chat = lazy(() => import('./components/Chat'));
const Inbox = lazy(() => import('./components/Inbox/Inbox'));
const Subscribers = lazy(() => import('./components/Subscribers'));
const SocialApps = lazy(() => import('./components/SocialApps'));
const InstagramDMAutomation = lazy(() => import('./components/InstagramAutomation/InstagramDMAutomation'));
const InstagramAutomationList = lazy(() => import('./components/InstagramAutomation/InstagramAutomationList'));
const Apps = lazy(() => import('./components/Apps'));
const CoursesList = lazy(() => import('./components/Courses/CoursesList'));
const CoursesDashboard = lazy(() => import('./components/Courses/CoursesDashboard'));
const CoursesSettings = lazy(() => import('./components/Courses/CoursesSettings'));
const CourseWizard = lazy(() => import('./components/Courses/CourseWizard'));
const CourseCheckout = lazy(() => import('./components/Courses/CourseCheckout'));
const CourseCheckoutSuccess = lazy(() => import('./components/Courses/CourseCheckoutSuccess'));
const VoiceTraining = lazy(() => import('./components/MyAIVoice/VoiceTraining'));
const VoicePlayground = lazy(() => import('./components/MyAIVoice/VoicePlayground'));
const VoiceHistory = lazy(() => import('./components/MyAIVoice/VoiceHistory'));
const DonationsDashboard = lazy(() => import('./components/Donations/DonationsDashboard'));
const DonationsList = lazy(() => import('./components/Donations/DonationsList'));
const DonationWizard = lazy(() => import('./components/Donations/DonationWizard'));
const DonationTransactions = lazy(() => import('./components/Donations/DonationTransactions'));
const DonationCheckout = lazy(() => import('./components/Donations/DonationCheckout'));
const DonationCheckoutSuccess = lazy(() => import('./components/Donations/DonationCheckoutSuccess'));
import SocialPage from './components/SocialPage';
import SocialAuthCallback from './components/SocialAuthCallback';
const ProductGallery = lazy(() => import('./components/Designer/ProductGallery'));
const Products = lazy(() => import('./components/Designer/Products'));
const ProductDetail = lazy(() => import('./components/Designer/ProductDetail'));
const CreateCheckoutLink = lazy(() => import('./components/Checkout/CreateCheckoutLink'));
const CheckoutLinksList = lazy(() => import('./components/Checkout/CheckoutLinksList'));
const DesignerPreview = lazy(() => import('./components/Designer/DesignerPreview').then(m => ({default: m.DesignerPreview})));
const BlogsList = lazy(() => import('./components/BlogsList'));
const ProfilePageDemo = lazy(() => import('./components/ProfilePage').then(m => ({default: m.ProfilePageDemo})));
const ProfileEditor = lazy(() => import('./components/ProfileEditor').then(m => ({default: m.ProfileEditor})));

// Admin routes (superadmin only)
const AdminLayout = lazy(() => import('./components/Admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./components/Admin/AdminDashboard'));
const AdminUsers = lazy(() => import('./components/Admin/AdminUsers'));
const AdminProfiles = lazy(() => import('./components/Admin/AdminProfiles'));
const AdminProfileWizard = lazy(() => import('./components/Admin/AdminProfileWizard'));
const AdminProfileKnowledgeBase = lazy(() => import('./components/Admin/AdminProfileKnowledgeBase'));
const AdminMerchantDetail = lazy(() => import('./components/Admin/AdminMerchantDetail'));
const AdminReferrals = lazy(() => import('./components/Admin/AdminReferrals'));
const AdminSubscriptions = lazy(() => import('./components/Admin/AdminSubscriptions'));

// Public claim page
const MerchantClaimPage = lazy(() => import('./components/MerchantClaimPage'));

// Sidebar is only used inside protected routes; lazy so it stays out of the
// public profile chunk.
const Sidebar = lazy(() => import('./components/Layout').then(m => ({default: m.Sidebar})));

// Layout wrapper with Sidebar
function SidebarLayout({children}) {
    return (
        <Sidebar>
            {children}
        </Sidebar>
    );
}

// Redirects out of the SPA to a non-/app/ URL on the Next.js SEO origin
// (e.g. sign-in / sign-up). In dev the SEO site runs on a different port, so we
// resolve the full URL via getSeoBaseUrl(). Replace so back button doesn't trap
// the user in a redirect loop.
function RedirectOut({to}) {
    React.useEffect(() => {
        const search = window.location.search || '';
        window.location.replace(`${getSeoBaseUrl()}${to}${search}`);
    }, [to]);
    return <AuthBounceSpinner />;
}

// Check for shared article view parameter
function AppWrapper() {
    const urlParams = new URLSearchParams(window.location.search);
    const viewArticleId = urlParams.get('view');

    // If viewing a shared article, render the ArticleViewer
    if (viewArticleId) {
        return (
            <AuthProvider>
                <Suspense fallback={null}>
                    <ArticleViewer articleId={viewArticleId} />
                </Suspense>
            </AuthProvider>
        );
    }

    // Otherwise render the normal app with routing
    return (
        <AuthProvider>
            <Toaster />
            <Router basename="/app">
                <Suspense fallback={null}>
                    <Routes>
                        {/* Public routes - no authentication required */}
                        {/* Sign-in / sign-up live in the Next.js seo app at /sign-in and /sign-up.
                            Anyone landing on /app/sign-in or /app/sign-up is bounced out of the SPA. */}
                        <Route element={<RedirectOut to="/sign-in" />} path="/sign-in" />
                        <Route element={<RedirectOut to="/sign-up" />} path="/sign-up" />
                        <Route element={<ForgotPasswordPage />} path="/forgot-password" />
                        <Route element={<ResetPasswordPage />} path="/reset-password" />
                        <Route element={<AuthCallbackPage />} path="/auth/callback" />
                        <Route element={<AuthResolve />} path="/auth/resolve" />
                        <Route element={<CalendlyCallback />} path="/calendly/callback" />
                        <Route element={<GumroadCallback />} path="/gumroad/installed" />
                        <Route element={<PublicProfile />} path="/p" />

                        {/* Public short-link resolver: /s/:code → resolve via API, then redirect */}
                        <Route element={<ShortLinkResolver />} path="/s/:code" />

                        {/* Public Globe - Embeddable analytics globe */}
                        <Route element={<PublicGlobe />} path="/embed/globe" />

                        {/* Preview Draft - for live preview without saving */}
                        <Route element={<PreviewDraft />} path="/preview-draft" />

                        {/* Shopify Template Landing — public template page (templateId in URL) */}
                        <Route element={<ShopifyTemplateLanding />} path="/template/shopify/:templateId" />
                        {/* Shopify Theme Landing — public theme preview page (themeId in URL) */}
                        <Route element={<ShopifyThemeLanding />} path="/theme/shopify/:themeId" />

                        {/* Onboarding Wizard - post signup */}
                        <Route element={
                            <ProtectedRoute>
                                <OnboardingWizard />
                            </ProtectedRoute>
                        } path="/onboarding" />

                        {/* Shopify OAuth callback during onboarding */}
                        <Route element={
                            <ProtectedRoute>
                                <OnboardingWizard />
                            </ProtectedRoute>
                        } path="/onboarding/shopify/callback" />

                        {/* Brand onboarding — separate 2-step flow (templates + Shopify)
                            triggered from the SEO /get-started entry point. Shares the
                            same Shopify callback pattern as the standard wizard. */}
                        <Route element={
                            <ProtectedRoute>
                                <BrandOnboardingWizard />
                            </ProtectedRoute>
                        } path="/brand-onboarding" />
                        <Route element={
                            <ProtectedRoute>
                                <BrandOnboardingWizard />
                            </ProtectedRoute>
                        } path="/brand-onboarding/shopify/callback" />

                        {/* Protected routes with Sidebar - require authentication */}
                        {/* Dashboard - Default route after login */}
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <Dashboard />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/dashboard" />

                        {/* Default route - redirect to dashboard */}
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <Dashboard />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/" />

                        {/* Social Apps — only visible in sidebar when user has ≥1 connected social platform */}
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <SocialApps />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/social-apps" />

                        {/* Social Auth Callback — popup window landing (public: auth checked inside component) */}
                        <Route element={<SocialAuthCallback />} path="/auth/social/callback" />

                        {/* Apps top-level — dedicated page that lists installable
                            integrations. Replaces the old reuse-shim that pointed at
                            AccountSettings forced into the integrations tab. */}
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <Apps />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/apps" />

                        {/* Courses (first-party app) — list + wizard. Sidebar
                            entry only appears when user.installedApps includes
                            'courses', but routes themselves stay registered so
                            in-flight wizard sessions don't 404 if uninstall
                            races a save. */}
                        {/* /courses defaults to the analytics dashboard;
                            the list of courses lives at /courses/list and
                            /courses/dashboard remains as an explicit alias. */}
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <CoursesDashboard />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/courses" />
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <CoursesDashboard />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/courses/dashboard" />
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <CoursesList />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/courses/list" />
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <CoursesSettings />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/courses/settings" />
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <CourseWizard />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/courses/new" />
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <CourseWizard />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/courses/:id/edit" />

                        {/* Public checkout pages — no auth, no sidebar.
                            Buyer lands here from the Buy Now button on a
                            course block in someone's bio profile. */}
                        <Route path="/courses/:id/checkout" element={<CourseCheckout />} />
                        <Route path="/courses/:id/checkout/success" element={<CourseCheckoutSuccess />} />

                        {/* AI Voice (first-party app). /voice defaults to
                            the Training tab. Backend endpoints land later —
                            for now the install flag + clip list are local. */}
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <VoiceTraining />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/voice" />
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <VoiceTraining />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/voice/training" />
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <VoicePlayground />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/voice/playground" />
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <VoiceHistory />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/voice/history" />
                        {/* Donations app — dashboard, list, wizard. Sidebar entry
                            only appears when user.installedApps includes 'donations'. */}
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <DonationsDashboard />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/donations" />
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <DonationsDashboard />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/donations/dashboard" />
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <DonationsList />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/donations/list" />
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <DonationWizard />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/donations/new" />
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <DonationWizard />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/donations/:id/edit" />

                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <DonationTransactions />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/donations/:campaignId/transactions" />

                        {/* Public donation checkout — slug format: campaign-name-id */}
                        <Route path="/donations/:slug/checkout" element={<DonationCheckout />} />
                        <Route path="/donations/:slug/checkout/success" element={<DonationCheckoutSuccess />} />

                        {/* Social App Pages — Calendar, Inbox, Accounts */}
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <SocialPage />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/apps/social/:section" />

                        {/* Performance Analytics - Static analytics page (kept but not linked from sidebar) */}
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <PerformanceAnalytics />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/analytics" />

                        {/* Financial Dashboard - New dark-style financial view */}
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <OverView />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/financial-dashboard" />

                        {/* Store Fronts - Profile Listing */}
                        <Route element={
                            <ProtectedRoute permission="storefronts">
                                <SidebarLayout>
                                    <MyPages />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/my-pages" />

                        {/* Knowledge Base */}
                        <Route element={
                            <ProtectedRoute permission="kw_base" requireKb>
                                <SidebarLayout>
                                    <KnowledgeSources />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/knowledge-sources" />

                        {/* Settings - Account Settings (tab is now a path param) */}
                        <Route element={
                            <ProtectedRoute permission="settings.general">
                                <SidebarLayout>
                                    <AccountSettings onBackToDashboard={() => window.location.href = '/app/dashboard'} />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/settings" />
                        <Route element={
                            <ProtectedRoute permission="settings.general">
                                <SidebarLayout>
                                    <AccountSettings onBackToDashboard={() => window.location.href = '/app/dashboard'} />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/settings/:tab" />

                        {/* Shopify OAuth callback from settings */}
                        <Route element={
                            <ProtectedRoute permission="settings.general">
                                <SidebarLayout>
                                    <AccountSettings onBackToDashboard={() => window.location.href = '/app/dashboard'} />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/settings/shopify/callback" />

                        {/* WhatsApp Chat widget admin (its own sub-sidebar) */}
                        <Route element={
                            <ProtectedRoute permission="settings.general">
                                <SidebarLayout>
                                    <WhatsAppWidget />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/whatsapp-chat" />
                        <Route element={
                            <ProtectedRoute permission="settings.general">
                                <SidebarLayout>
                                    <WhatsAppWidget />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/whatsapp-chat/:screen" />

                        {/* Designer - Bio Profile Builder (BlockNote-based) */}
                        <Route element={
                            <ProtectedRoute permission="storefronts">
                                <SidebarLayout>
                                    <BlockNoteDesigner />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/designer" />

                        {/* Bio Profile Editor - Simplified bio link page editor */}
                        <Route element={
                            <ProtectedRoute permission="storefronts">
                                <SidebarLayout>
                                    <BioProfileEditor />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/bio-editor" />

                        <Route element={
                            <ProtectedRoute permission="storefronts">
                                <SidebarLayout>
                                    <Template />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/template" />

                        {/* Brands — shared commission-product catalog (BrandProducts).
                            The old Shopify-Collective Brands page component still exists
                            but is no longer routed here. */}
                        <Route element={
                            <ProtectedRoute>
                                <SidebarLayout>
                                    <BrandProducts />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/brands" />

                        {/* Orders & Transactions (Shopify) */}
                        <Route element={
                            <ProtectedRoute permission="orders">
                                <SidebarLayout>
                                    <OrdersList />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/orders" />
                        <Route element={
                            <ProtectedRoute permission="transactions">
                                <SidebarLayout>
                                    <TransactionsList />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/transactions" />

                        {/* Earnings */}
                        <Route element={
                            <ProtectedRoute permission="earnings">
                                <SidebarLayout>
                                    <Earnings />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/earnings" />

                        {/* Refer & Earn */}
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <ReferAndEarn />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/refer" />

                        {/* Instagram DM Automation — UI-only pages for now */}
                        {/* Temporarily hidden — commented out, not removed.
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <InstagramAutomationList />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/instagram-dm-automation" />

                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <InstagramDMAutomation />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/instagram-dm-automation/:id" />
                        */}


                        {/* Chat */}
                        <Route element={
                            <ProtectedRoute permission="inbox">
                                <SidebarLayout>
                                    <Chat />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/chat" />

                        {/* Unified Inbox (new myalice-style inbox) */}
                        <Route element={
                            <ProtectedRoute permission="inbox">
                                <SidebarLayout>
                                    <Inbox />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/inbox/:view?" />

                        {/* Subscribers */}
                        <Route element={
                            <ProtectedRoute permission="subscribers">
                                <SidebarLayout>
                                    <Subscribers />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/subscribers" />

                        {/* Product Gallery - Detail page for a brand */}
                        <Route element={
                            <ProtectedRoute permission="products">
                                <SidebarLayout>
                                    <ProductGallery />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/product-gallery/:brandId" />

                        {/* Products - All products listing */}
                        <Route element={
                            <ProtectedRoute permission="products">
                                <SidebarLayout>
                                    <Products />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/products" />

                        {/* Product Detail - Individual product page */}
                        <Route element={
                            <ProtectedRoute permission="products">
                                <SidebarLayout>
                                    <ProductDetail />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/product/:productId" />

                        {/* Links - Quick checkout listing (open to all authenticated users) */}
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <CheckoutLinksList />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/links" />

                        {/* Links - Create checkout link (open to all authenticated users) */}
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <CreateCheckoutLink />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/links/checkout/new" />

                        {/* Links - Edit existing checkout link (open to all authenticated users) */}
                        <Route element={
                            <ProtectedRoute permission="dashboard">
                                <SidebarLayout>
                                    <CreateCheckoutLink />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/links/checkout/:id" />

                        {/* Designer Preview - Full screen without sidebar */}
                        <Route element={
                            <ProtectedRoute>
                                <DesignerPreview />
                            </ProtectedRoute>
                        } path="/designer/preview" />

                        {/* BlockNote Designer Preview */}
                        <Route element={
                            <ProtectedRoute>
                                <BlockNoteDesignerPreview />
                            </ProtectedRoute>
                        } path="/blocknote-designer/preview" />

                        {/* Blog Routes */}
                        <Route element={
                            <ProtectedRoute>
                                <SidebarLayout>
                                    <BlogsList />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/blogs" />

                        {/* Profile Routes */}
                        <Route element={
                            <ProtectedRoute>
                                <SidebarLayout>
                                    <ProfilePageDemo />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/profile" />

                        <Route element={
                            <ProtectedRoute>
                                <SidebarLayout>
                                    <ProfileEditor />
                                </SidebarLayout>
                            </ProtectedRoute>
                        } path="/profile/edit" />

                        {/* ─── Superadmin routes ─── */}
                        <Route element={
                            <AdminRoute>
                                <AdminLayout>
                                    <AdminDashboard />
                                </AdminLayout>
                            </AdminRoute>
                        } path="/admin" />
                        <Route element={
                            <AdminRoute>
                                <AdminLayout>
                                    <AdminUsers type="superadmin" />
                                </AdminLayout>
                            </AdminRoute>
                        } path="/admin/users" />
                        <Route element={
                            <AdminRoute>
                                <AdminLayout>
                                    <AdminUsers type="merchants" />
                                </AdminLayout>
                            </AdminRoute>
                        } path="/admin/merchants" />
                        <Route element={
                            <AdminRoute>
                                <AdminLayout>
                                    <AdminMerchantDetail />
                                </AdminLayout>
                            </AdminRoute>
                        } path="/admin/merchants/:id" />
                        <Route element={
                            <AdminRoute>
                                <AdminLayout>
                                    <AdminProfiles />
                                </AdminLayout>
                            </AdminRoute>
                        } path="/admin/profiles" />
                        <Route element={
                            <AdminRoute>
                                <AdminLayout>
                                    <AdminReferrals />
                                </AdminLayout>
                            </AdminRoute>
                        } path="/admin/referrals" />
                        <Route element={
                            <AdminRoute>
                                <AdminLayout>
                                    <AdminSubscriptions />
                                </AdminLayout>
                            </AdminRoute>
                        } path="/admin/subscriptions" />
                        <Route element={
                            <AdminRoute>
                                <AdminLayout>
                                    <AdminProfileWizard />
                                </AdminLayout>
                            </AdminRoute>
                        } path="/admin/profiles/new" />
                        <Route element={
                            <AdminRoute>
                                <AdminLayout>
                                    <BioProfileEditor />
                                </AdminLayout>
                            </AdminRoute>
                        } path="/admin/profiles/edit" />
                        <Route element={
                            <AdminRoute>
                                <AdminLayout>
                                    <AdminProfileKnowledgeBase />
                                </AdminLayout>
                            </AdminRoute>
                        } path="/admin/profiles/:id/knowledge-base" />
                        <Route element={
                            <AdminRoute>
                                <AdminLayout>
                                    <AccountSettings onBackToDashboard={() => window.location.href = '/app/admin'} hideTabs={['apps', 'billing', 'deleteAccount']} />
                                </AdminLayout>
                            </AdminRoute>
                        } path="/admin/settings" />

                        {/* Public claim page - merchant claims admin-built profile */}
                        <Route element={<MerchantClaimPage />} path="/claim/:token" />

                        {/* Public profile by slug (mydomain/profile-slug) – must be last */}
                        <Route element={<BlockNotePublicProfile />} path="/:slug" />
                    </Routes>
                </Suspense>
            </Router>
        </AuthProvider>
    );
}

ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
        <MantineProvider>
            <AppWrapper />
        </MantineProvider>
    </React.StrictMode>
);
